import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  // CircularProgress removed — replaced with skeleton
  Alert,
  useTheme,
  useMediaQuery,
  Chip
} from '@mui/material';
import {
  CalendarToday,
  Schedule,
  LocationOn
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';

import { WeeklyCalendarSkeleton } from './ScheduleSkeletons';
import { scheduleService, ScheduleMatch, DepartureTimeInfo } from '@/services/scheduleService';
import { generateConflictDescription } from '@/utils/conflictFormatter';
import { playerService } from '@/services/playerService';
import { useOrgConflicts } from '@/hooks/useOrgConflicts';
import { useCalendarAutoRefresh } from '@/hooks/useCalendarAutoRefresh';
import AgendaDialog, { AgendaMatch } from './AgendaDialog';
import { TeamLogo } from './TeamLogo';
import { getTimezoneAbbreviation, getEffectiveTimezone, formatInUserTimezone } from '@/utils/timezoneUtils';
import { RootState } from '@/store';
import { PLAYER_COLORS, getPlayerColorMap } from '@/utils/playerColors';

// Extend dayjs with plugins
dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  allDay?: boolean;
  classNames?: string[];
  extendedProps: {
    match: ScheduleMatch;
    playerColor: string;
    hasConflict: boolean;
    isTBD?: boolean;
  };
}

const WeeklyCalendar: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState<number>(0); // Store scroll position
  const calendarRef = useRef<FullCalendar>(null);
  
  // Redux selectors for timezone data
  const user = useSelector((state: RootState) => state.auth.user);
  const orgSettings = useSelector((state: RootState) => state.organization.settings);
  
  // Use Material-UI theme for responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 768px
  
  // Always use current week - no pagination needed
  // IMPORTANT: Use the user's effective timezone to determine "today" and the current week.
  // Without this, a UTC system clock could show Monday when the user is still on Sunday.
  const userTz = getEffectiveTimezone(user, orgSettings);
  const nowInUserTz = dayjs().tz(userTz);
  const currentWeek = nowInUserTz.startOf('isoWeek').format('YYYY-MM-DD'); // Monday start

  // Enable automatic calendar refresh when data changes
  useCalendarAutoRefresh();

  // Determine initial view based on screen size
  const getInitialView = (): string => {
    return isMobile ? 'listWeek' : 'dayGridWeek';
  };

  // Handle responsive view switching
  useEffect(() => {
    const calendar = calendarRef.current?.getApi();
    if (calendar) {
      const newView = isMobile ? 'listWeek' : 'dayGridWeek';
      if (calendar.view.type !== newView) {
        calendar.changeView(newView);
      }
    }
  }, [isMobile]);

  // Prevent scroll to top when agenda dialog opens - using useLayoutEffect for immediate execution
  useLayoutEffect(() => {
    if (agendaOpen && scrollPosition > 0) {
      // Restore scroll position immediately when dialog opens
      window.scrollTo(0, scrollPosition);
      
      // Also prevent any subsequent scroll events for a brief moment
      const preventScroll = (e: Event) => {
        e.preventDefault();
        window.scrollTo(0, scrollPosition);
      };
      
      // Add scroll prevention for a very brief moment
      document.addEventListener('scroll', preventScroll, { passive: false });
      
      // Remove the scroll prevention after a short delay
      const timeout = setTimeout(() => {
        document.removeEventListener('scroll', preventScroll);
      }, 100);
      
      return () => {
        clearTimeout(timeout);
        document.removeEventListener('scroll', preventScroll);
      };
    }
  }, [agendaOpen, scrollPosition]);

  // Fetch all players for stable color mapping
  const { data: allPlayers = [] } = useQuery(
    'players',
    () => playerService.getPlayers(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes - players don't change often
      cacheTime: 1000 * 60 * 30, // 30 minutes
    }
  );

  // Fetch departure times when players or schedule data changes
  // Fetch schedule data for the current week
  const { data: scheduleMatches, isLoading: scheduleLoading, error: scheduleError } = useQuery(
    ['schedule-weekly', currentWeek],
    () => {
      // Calculate the current week range (Monday to Sunday)
      const weekStart = dayjs(currentWeek).startOf('isoWeek'); // Monday
      const weekEnd = weekStart.add(6, 'days'); // Sunday

      // Add ±1 day buffer to the API query to account for timezone boundaries.
      // The backend filters using UTC dates, so matches in the evening of the
      // last day (in western timezones) can fall into the next UTC day. FullCalendar
      // only renders events within the visible week, so extra matches are harmless.
      return scheduleService.getSchedule({
        startDate: weekStart.subtract(1, 'day').format('YYYY-MM-DD'),
        endDate: weekEnd.add(1, 'day').format('YYYY-MM-DD'),
        playerIds: allPlayers?.map(p => p.id) || []
      });
    },
    {
      enabled: allPlayers.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Fetch conflict data (shared with DashboardPage via useOrgConflicts)
  const { data: conflictData } = useOrgConflicts(allPlayers.length > 0);

  // Create filtered player list for legend (only players with matches in current view)
  const playersInCurrentView = useMemo(() => {
    if (!scheduleMatches || scheduleMatches.length === 0) return [];
    
    const playersInView = new Set<string>();
    scheduleMatches.forEach((match: any) => {
      if (match.players) {
        match.players.forEach((player: any) => {
          playersInView.add(player.name);
        });
      }
    });
    
    // Return sorted array of player names that have matches in current view
    return Array.from(playersInView).sort();
  }, [scheduleMatches]);

  // Create stable player color mapping using all players (stored colors first, then auto-assign)
  const playerColorMap = useMemo(() => {
    if (!allPlayers || allPlayers.length === 0) return {};
    return getPlayerColorMap(allPlayers);
  }, [allPlayers]);

  // Create conflict lookup for individual matches with severity information
  const conflictLookup = useMemo(() => {
    if (!conflictData?.conflictGroups) return new Map<string, { hasConflict: boolean; severity?: string }>();
    
    const conflictMap = new Map<string, { hasConflict: boolean; severity?: string }>();
    conflictData.conflictGroups.forEach(group => {
      group.conflicts.forEach(conflict => {
        const match1Id = conflict.match1.match.id;
        const match2Id = conflict.match2.match.id;
        
        // Mark both matches as having conflicts with severity
        conflictMap.set(match1Id, { hasConflict: true, severity: conflict.severity });
        conflictMap.set(match2Id, { hasConflict: true, severity: conflict.severity });
      });
    });
    
    return conflictMap;
  }, [conflictData]);

  // Build mobile agenda: group matches by date for the current week
  const mobileAgendaDays = useMemo(() => {
    if (!scheduleMatches || scheduleMatches.length === 0) return [];

    const weekStart = dayjs(currentWeek).startOf('isoWeek');
    const weekEnd = weekStart.add(6, 'days');

    // Collect unique matches (deduplicated by match ID), with player info merged
    const matchMap = new Map<string, {
      match: any;
      players: { name: string; color: string; isHome: boolean }[];
      isTBD: boolean;
      dateInUserTz: string;
    }>();

    scheduleMatches.forEach((match: any) => {
      if (!match.players || match.players.length === 0) return;
      if (match.status === 'POSTPONED') return;
      if (!match.dateTime && match.status !== 'TBD') return;

      let isTBDMatch = match.status === 'TBD';
      if (match.dateTime) {
        const matchTimeUTC = dayjs.utc(match.dateTime);
        const hour = matchTimeUTC.hour();
        const minute = matchTimeUTC.minute();
        if ((hour === 0 && minute === 0) || (hour === 0 && minute === 1)) {
          isTBDMatch = true;
        }
      }

      if (!match.dateTime) return;

      const dateInUserTz = isTBDMatch
        ? dayjs.utc(match.dateTime).format('YYYY-MM-DD')
        : dayjs.utc(match.dateTime).tz(userTz).format('YYYY-MM-DD');

      // Filter to current week
      const matchDay = dayjs(dateInUserTz);
      if (matchDay.isBefore(weekStart, 'day') || matchDay.isAfter(weekEnd, 'day')) return;

      const existing = matchMap.get(match.id);
      if (existing) {
        match.players.forEach((player: any) => {
          const alreadyAdded = existing.players.some(p => p.name === player.name);
          if (!alreadyAdded) {
            existing.players.push({
              name: player.name,
              color: playerColorMap[player.name] || PLAYER_COLORS[0],
              isHome: player.isHome
            });
          }
        });
      } else {
        matchMap.set(match.id, {
          match,
          players: match.players.map((p: any) => ({
            name: p.name,
            color: playerColorMap[p.name] || PLAYER_COLORS[0],
            isHome: p.isHome
          })),
          isTBD: isTBDMatch,
          dateInUserTz
        });
      }
    });

    // Group by date
    const dayMap = new Map<string, typeof matchMap extends Map<string, infer V> ? V[] : never>();
    matchMap.forEach((entry) => {
      const dateKey = entry.dateInUserTz;
      if (!dayMap.has(dateKey)) dayMap.set(dateKey, []);
      dayMap.get(dateKey)!.push(entry);
    });

    // Sort days, then sort matches within each day
    return Array.from(dayMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, matches]) => ({
        date,
        matches: matches.sort((a, b) => {
          if (a.isTBD && !b.isTBD) return 1;
          if (!a.isTBD && b.isTBD) return -1;
          if (!a.match.dateTime || !b.match.dateTime) return 0;
          return dayjs(a.match.dateTime).isBefore(dayjs(b.match.dateTime)) ? -1 : 1;
        })
      }));
  }, [scheduleMatches, currentWeek, userTz, playerColorMap]);

  // Format time for mobile agenda — includes timezone or "Venue Time"
  const formatAgendaTime = (dateTime: string | null, isTBD: boolean, timezoneVerified?: boolean) => {
    if (!dateTime || isTBD) return 'TBD';
    const time = formatInUserTimezone(dateTime, 'h:mm A', false, user, orgSettings);
    const tzLabel = timezoneVerified === false ? 'Venue Time' : getTimezoneAbbreviation(user, orgSettings, dateTime);
    return `${time} ${tzLabel}`;
  };

  // Get departure time for a specific match (only upcoming matches)
  const getDepartureTimeForMatch = (match: any): DepartureTimeInfo | null => {
    if (!match.players || match.players.length === 0) return null;
    if (!match.departureTimes) return null;
    // Don't show departure time for matches that have already occurred
    if (match.dateTime && dayjs(match.dateTime).add(2, 'hour').isBefore(dayjs())) return null;

    const playerId = match.players[0].id;
    return match.departureTimes[playerId] || null;
  };

  // Transform schedule data into FullCalendar events
  const calendarEvents: CalendarEvent[] = useMemo(() => {
    const dataToUse = scheduleMatches;
    if (!dataToUse || dataToUse.length === 0) {
      return [];
    }

    const events: CalendarEvent[] = [];

    dataToUse.forEach((match: any) => {
      
      if (!match.players || match.players.length === 0) {
        return;
      }

      // Skip postponed matches - they won't take place on the scheduled date
      if (match.status === 'POSTPONED') {
        return;
      }

      // Skip matches that have no date AND are not TBD with a scheduled date
      if (!match.dateTime && match.status !== 'TBD') {
        return;
      }

      match.players.forEach((player: any) => {
        const playerColor = playerColorMap[player.name] || PLAYER_COLORS[0];
        const conflictInfo = conflictLookup.get(match.id);
        const hasConflict = conflictInfo?.hasConflict || false;
        
        // Use the isHome property from the player data
        const isHome = player.isHome;
        const opponent = isHome ? match.awayTeam : match.homeTeam;

        // Create title with v/@ prefix (Google Calendar style)
        const vsAt = isHome ? 'v' : '@';
        let title = `${vsAt} ${opponent}`;
        
        // Check if this is a TBD match (midnight UTC time or explicit TBD status)
        let isTBDMatch = match.status === 'TBD';
        if (match.dateTime) {
          const matchTimeUTC = dayjs.utc(match.dateTime);
          const hour = matchTimeUTC.hour();
          const minute = matchTimeUTC.minute();

          if ((hour === 0 && minute === 0) || (hour === 0 && minute === 1)) {
            isTBDMatch = true;
          }
        }

        // Add TBD prefix to title for TBD matches
        if (isTBDMatch) {
          title = `TBD ${title}`;
        }

        // Store original dateTime for sorting
        const originalDateTime = match.dateTime;
        let eventStart: string;

        if (match.dateTime) {
          // Use just the date part so events render as all-day bars
          const matchUserTz = getEffectiveTimezone(user, orgSettings);
          eventStart = isTBDMatch
            ? dayjs.utc(match.dateTime).format('YYYY-MM-DD')
            : dayjs.utc(match.dateTime).tz(matchUserTz).format('YYYY-MM-DD');
        } else if (isTBDMatch) {
          // Skip TBD matches with no date at all
          return;
        } else {
          // Handle matches with no dateTime at all
          return;
        }

        // Use proper FullCalendar event format
        // Determine text color based on background color for optimal contrast
        const getTextColor = (bgColor: string) => {
          // Light colors that need dark text
          const lightColors = ['#ECE133', '#FBAFE4', '#56B4E9'];
          return lightColors.includes(bgColor) ? '#000000' : '#ffffff';
        };

        // Get departure time for this match
        const departureTime = getDepartureTimeForMatch(match);

        const event = {
          id: `${match.id}-${player.id}`,
          title: title,
          start: eventStart,
          allDay: true,
          backgroundColor: playerColor,
          borderColor: playerColor,
          textColor: getTextColor(playerColor),
          classNames: [
            ...(hasConflict ? ['conflict-indicator'] : []),
            ...(isTBDMatch ? ['tbd-event'] : [])
          ],
          extendedProps: {
            match,
            playerColor,
            hasConflict,
            conflictSeverity: conflictInfo?.severity,
            isTBD: isTBDMatch,
            departureTime: departureTime,
            originalDateTime: originalDateTime
          }
        };

        events.push(event);
      });
    });

    return events;
  }, [scheduleMatches, playerColorMap, conflictLookup]);

  const handleDateClick = (info: any) => {
    // Store current scroll position before opening dialog
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
    setScrollPosition(currentScroll);
    
    // Use requestAnimationFrame to ensure scroll position is maintained
    requestAnimationFrame(() => {
      setSelectedDate(info.dateStr);
      setAgendaOpen(true);
      
      // Ensure scroll position is maintained after state updates
      requestAnimationFrame(() => {
        window.scrollTo(0, currentScroll);
      });
    });
  };

  const handleEventClick = (info: any) => {
    // Store current scroll position before opening dialog
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
    setScrollPosition(currentScroll);
    
    // Use requestAnimationFrame to ensure scroll position is maintained
    requestAnimationFrame(() => {
      // Extract date from the clicked event
      const eventDate = dayjs(info.event.start).format('YYYY-MM-DD');
      setSelectedDate(eventDate);
      setAgendaOpen(true);
      
      // Ensure scroll position is maintained after state updates
      requestAnimationFrame(() => {
        window.scrollTo(0, currentScroll);
      });
    });
  };

  const handleCloseAgenda = () => {
    setAgendaOpen(false);
    // Restore scroll position after dialog closes
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
      setSelectedDate(null);
    }, 120);
  };

  // Helper function to get conflict between two specific matches
  const getConflictBetweenMatches = (match1: AgendaMatch, match2: AgendaMatch) => {
    if (!conflictData?.conflictGroups) return null;
    
    for (const group of conflictData.conflictGroups) {
      for (const conflict of group.conflicts) {
        const conflictMatch1Id = conflict.match1.match.id;
        const conflictMatch2Id = conflict.match2.match.id;
        
        // Extract match IDs from our agenda match IDs (format: "matchId-playerId")
        const match1Id = match1.id.split('-')[0];
        const match2Id = match2.id.split('-')[0];
        
        // Check if these two matches are in conflict with each other
        if ((conflictMatch1Id === match1Id && conflictMatch2Id === match2Id) ||
            (conflictMatch1Id === match2Id && conflictMatch2Id === match1Id)) {
          return {
            severity: conflict.severity,
            description: generateConflictDescription(conflict),
            timeDifference: conflict.timeConflict?.timeDifference,
            availableTravelWindow: conflict.timeConflict?.availableTravelWindow,
            travelTime: conflict.timeConflict?.travelTime,
            type: conflict.timeConflict?.type
          };
        }
      }
    }
    return null;
  };

  // Get agenda matches for selected date - matching full calendar structure
  const selectedDateMatches: AgendaMatch[] = useMemo(() => {
    if (!selectedDate || !scheduleMatches) return [];

    const dayMatches: AgendaMatch[] = [];

    scheduleMatches.forEach((match: any) => {
      if (!match.players || match.players.length === 0) return;

      // Check if this is a TBD match first
      let isTBDMatch = match.status === 'TBD';
      if (match.dateTime) {
        const matchTimeUTC = dayjs.utc(match.dateTime);
        const hour = matchTimeUTC.hour();
        const minute = matchTimeUTC.minute();

        if ((hour === 0 && minute === 0) || (hour === 0 && minute === 1)) {
          isTBDMatch = true;
        }
      }

      // Use appropriate timezone for date formatting
      const matchDate = match.dateTime ?
        (isTBDMatch ? dayjs.utc(match.dateTime).format('YYYY-MM-DD') : dayjs(match.dateTime).format('YYYY-MM-DD'))
        : null;
      if (matchDate !== selectedDate) return;

      match.players.forEach((player: any) => {
        const playerColor = playerColorMap[player.name] || PLAYER_COLORS[0];
        const conflictInfo = conflictLookup.get(match.id);
        const hasConflict = conflictInfo?.hasConflict || false;
        const departureTime = getDepartureTimeForMatch(match);
        
        dayMatches.push({
          id: `${match.id}-${player.id}`,
          dateTime: match.dateTime,
          homeTeam: match.homeTeam,
          awayTeam: match.awayTeam,
          isHome: player.isHome,
          location: match.location,
          league: match.league.name,
          division: match.league.division,
          player: player.name,
          playerColor,
          hasConflict,
          conflictSeverity: conflictInfo?.severity,
          status: match.status,
          homeTeamLogo: match.homeTeamLogo,
          awayTeamLogo: match.awayTeamLogo,
          departureTime: departureTime || undefined
        });
      });
    });

    return dayMatches.sort((a, b) => {
      if (!a.dateTime && !b.dateTime) return 0;
      if (!a.dateTime) return 1;
      if (!b.dateTime) return -1;
      return dayjs(a.dateTime).isBefore(dayjs(b.dateTime)) ? -1 : 1;
    });
  }, [selectedDate, scheduleMatches, playerColorMap, conflictLookup]);

  if (scheduleError) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            Failed to load schedule data. Please try again later.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card
        sx={{
          height: 'auto', // Full dynamic height based on content
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          // Remove overflow: 'auto' to prevent scrollbars
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: '#43A047' }} />
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarToday />
              <Typography variant="h6" component="div">
                This Week's Schedule
              </Typography>
              {!isMobile && (
                <Chip
                  label={getTimezoneAbbreviation(user, orgSettings)}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </CardContent>
        
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pt: 0, ...(isMobile && { pb: '8px !important' }) }}>
          {scheduleLoading ? (
            <Box py={1}>
              <WeeklyCalendarSkeleton />
            </Box>
          ) : isMobile ? (
            /* Mobile: Custom agenda view with MatchCard-style cards */
            <Box>
              {mobileAgendaDays.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" color="text.secondary" fontStyle="italic">
                    No matches this week
                  </Typography>
                </Box>
              ) : (
                mobileAgendaDays.map((day, dayIdx) => (
                  <Box key={day.date} sx={{ mb: dayIdx < mobileAgendaDays.length - 1 ? 2 : 0 }}>
                    {/* Date header */}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        borderBottom: '2px solid',
                        borderColor: 'text.primary',
                        pb: 0.5,
                        mb: 1
                      }}
                    >
                      {dayjs(day.date).format('dddd, MMMM D')}
                    </Typography>

                    {/* Match cards */}
                    {day.matches.map((entry, idx) => {
                      const { match, players, isTBD } = entry;
                      return (
                        <Box
                          key={match.id}
                          sx={{
                            py: 1.5,
                            borderBottom: idx < day.matches.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                          }}
                        >
                          {/* Time row */}
                          <Box sx={{ mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Schedule sx={{ fontSize: 16 }} color="action" />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatAgendaTime(match.dateTime, isTBD, match.timezoneVerified)}
                            </Typography>
                          </Box>

                          {/* Teams */}
                          <Box sx={{ mb: 0.75 }}>
                            {/* Home Team */}
                            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                              <TeamLogo
                                teamName={match.homeTeam}
                                logoPath={match.homeTeamLogo?.logoPath}
                                logoUrl={match.homeTeamLogo?.logoUrl}
                                size={24}
                              />
                              <Typography variant="body2">
                                {match.homeTeam}
                              </Typography>
                            </Box>
                            {/* Away Team */}
                            <Box display="flex" alignItems="center" gap={1}>
                              <TeamLogo
                                teamName={match.awayTeam}
                                logoPath={match.awayTeamLogo?.logoPath}
                                logoUrl={match.awayTeamLogo?.logoUrl}
                                size={24}
                              />
                              <Typography variant="body2">
                                {match.awayTeam}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Location */}
                          {match.location && (
                            <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                              <LocationOn sx={{ fontSize: 14 }} color="action" />
                              <Typography variant="caption" color="text.secondary">
                                {match.location}
                              </Typography>
                            </Box>
                          )}

                          {/* Player and home/away info */}
                          <Typography variant="caption" color="text.secondary">
                            {players.map((p) =>
                              players.length > 1 ? `${p.name} (${p.isHome ? 'Home' : 'Away'})` : p.name
                            ).join(', ')}
                            {players.length === 1 && ` · ${players[0].isHome ? 'Home' : 'Away'}`}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ))
              )}
            </Box>
          ) : (
            /* Desktop: FullCalendar grid view */
            <Box sx={{ flex: 1, minHeight: 0 }} className="weekly-calendar-container">
              <FullCalendar
                ref={calendarRef}
                key="weekly-calendar-stable"
                plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
                initialView={getInitialView()}
                initialDate={nowInUserTz.format('YYYY-MM-DD')}
                now={nowInUserTz.format('YYYY-MM-DD')}
                firstDay={1}
                timeZone={getEffectiveTimezone(user, orgSettings)}
                headerToolbar={{
                  left: '',
                  center: '',
                  right: ''
                }}
                eventSources={[
                  {
                    events: calendarEvents,
                    id: 'weekly-schedule-events'
                  }
                ]}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                contentHeight="auto"
                aspectRatio={0}
                dayMaxEvents={6}
                moreLinkClick="none"
                showNonCurrentDates={false}
                fixedWeekCount={false}
                navLinks={false}
                selectable={false}
                eventOrder={(a: any, b: any) => {
                  const aTime = a.extendedProps?.originalDateTime;
                  const bTime = b.extendedProps?.originalDateTime;
                  const aTBD = a.extendedProps?.isTBD;
                  const bTBD = b.extendedProps?.isTBD;
                  if (aTBD && !bTBD) return 1;
                  if (!aTBD && bTBD) return -1;
                  if (!aTime && !bTime) return 0;
                  if (!aTime) return 1;
                  if (!bTime) return -1;
                  return dayjs(aTime).isBefore(dayjs(bTime)) ? -1 : 1;
                }}
                listDayFormat={{ weekday: 'long', month: 'long', day: 'numeric' }}
                listDaySideFormat={false}
                noEventsContent="No matches this week"
              />
            </Box>
          )}
          
          {/* Player Legend - Bottom Right - matching full calendar exactly */}
          {!isMobile && playersInCurrentView.length > 0 && calendarEvents.length > 0 && (
            <Box sx={{ 
              mt: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, flexShrink: 0, fontSize: '0.8rem' }}>
                Players:
              </Typography>
              {playersInCurrentView.map((playerName, index) => (
                <Box
                  key={playerName}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      backgroundColor: playerColorMap[playerName] || PLAYER_COLORS[0],
                      borderRadius: '50%',
                      flexShrink: 0
                    }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                    {playerName}
                  </Typography>
                  {index < playersInCurrentView.length - 1 && (
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', mx: 0.5 }}>
                      •
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Shared Agenda Dialog */}
      <AgendaDialog
        open={agendaOpen}
        onClose={handleCloseAgenda}
        selectedDate={selectedDate}
        matches={selectedDateMatches}
        getConflictBetweenMatches={getConflictBetweenMatches}
      />

      {/* Custom CSS for calendar styling - matching FullCalendarDashboard */}
      <style>
        {`
          /* Scoped FullCalendar demo styles - same as FullCalendarDashboard */
          .weekly-calendar-container {
            font-family: Arial, Helvetica Neue, Helvetica, sans-serif;
            font-size: 14px;
          }
          
          /* Remove pulsating animation for conflict indicators */
          .conflict-indicator {
            /* No animation */
          }
          
          /* Make FullCalendar title consistent with dashboard headers */
          .fc-toolbar-title {
            font-size: 1.25rem !important; /* h6 variant size */
            font-weight: 500 !important;
            line-height: 1.6 !important;
          }
          
          /* Uniform cell heights - same as FullCalendarDashboard */
          .fc-daygrid-day {
            height: 120px !important;
          }
          
          /* Eliminate scrollbars from FullCalendar - same as FullCalendarDashboard */
          .fc-scroller {
            overflow: visible !important;
          }
          
          .fc-daygrid-body {
            overflow: visible !important;
          }
          
          .fc-view-harness {
            overflow: visible !important;
          }
          
          /* Event title styling - match FullCalendar */
          .fc-event {
            overflow: hidden !important;
          }
          
          .fc-event-title {
            overflow: hidden !important;
            font-size: 0.75rem !important;
            line-height: 1.1 !important;
          }
          
          /* Ensure proper event padding */
          .fc-event-main {
            padding: 1px 2px !important;
          }
          
          /* No events message styling */
          .fc-list-empty {
            text-align: center !important;
            padding: 40px 20px !important;
            color: #666 !important;
            font-style: italic !important;
          }
          
          /* Allow FullCalendar to expand fully without height constraints */
          .fc {
            height: auto !important;
          }
          
          /* Remove height constraints from specific views */
          .fc-list-week,
          .fc-daygrid {
            height: auto !important;
          }
        `}
      </style>
    </>
  );
};

export default WeeklyCalendar;
